import React, { useCallback, useEffect, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classname from 'classnames';
import throttle from 'lodash/throttle';
import { colors, positions, sizes } from "../../../enums";
import { getTooltipCoordinates, matchesPosition, POINTER_ROTATION } from "./utils.js";
/**
 * Rendering the tooltip itself, with text, pointer and box.
 * */

var AdaptiveTooltipContent = function AdaptiveTooltipContent(_ref) {
  var visible = _ref.visible,
      children = _ref.children,
      size = _ref.size,
      isCompact = _ref.isCompact,
      hoveredElementRef = _ref.hoveredElementRef,
      position = _ref.position,
      isInverted = _ref.isInverted,
      color = _ref.color,
      boundaryElement = _ref.boundaryElement,
      targetOffset = _ref.targetOffset,
      tooltipRef = _ref.tooltipRef,
      floatingPointer = _ref.floatingPointer;

  var _useState = useState({
    left: 0,
    top: 0,
    pointerX: 0,
    pointerY: 0,
    position: positions.TOP
  }),
      coordinates = _useState[0],
      setCoordinates = _useState[1]; //keeps track of the DOM ref where the tooltip need to render that gets re-positioned


  var getNextTooltipCoordinates = useCallback(function (position) {
    //grabbing the tooltip DOM
    var tooltipBounding = tooltipRef.current.getBoundingClientRect();
    var childrenBounding;

    if (hoveredElementRef.children && hoveredElementRef.children[0]) {
      childrenBounding = hoveredElementRef.children[0].getBoundingClientRect();
    } else {
      childrenBounding = hoveredElementRef.getBoundingClientRect();
    } //calculating and setting the coordinates where the tooltip will be displayed at


    return getTooltipCoordinates({
      position: position,
      prevPosition: coordinates.position,
      childrenBounding: childrenBounding,
      tooltipBounding: tooltipBounding,
      boundaryElement: boundaryElement,
      targetOffset: targetOffset,
      floatingPointer: floatingPointer
    });
  }, [coordinates]); //this will help us target and grab the tooltip element itself and then we can calculate coordinates on how to properly place it

  var handleSetCoordinates = useCallback(function (position) {
    var newCoordinates = getNextTooltipCoordinates(position);
    setCoordinates(newCoordinates);
  }, [hoveredElementRef, tooltipRef]);
  var handleTooltipRepositioning = useCallback(throttle(function (event) {
    if (hoveredElementRef.current && tooltipRef.current !== event.target) {
      handleSetCoordinates(position);
    }
  }, 200), [handleSetCoordinates, tooltipRef, hoveredElementRef]);
  useEffect(function () {
    var body = window.document.body.ownerDocument.body;

    if (visible) {
      body.addEventListener('resize', handleTooltipRepositioning);
      body.addEventListener('mousewheel', handleTooltipRepositioning); //when the tooltip is visible we need to re-position the tooltip as the user scrolls to keep it tracking the element the user is currently hovering over

      body.addEventListener('scroll', handleTooltipRepositioning);
    } else {
      body.removeEventListener('scroll', handleTooltipRepositioning);
      body.removeEventListener('resize', handleTooltipRepositioning);
      body.removeEventListener('mousewheel', handleTooltipRepositioning);
    }

    return function () {
      body.removeEventListener('resize', handleTooltipRepositioning);
      body.removeEventListener('mousewheel', handleTooltipRepositioning);
      body.removeEventListener('scroll', handleTooltipRepositioning);
    };
  }, [handleTooltipRepositioning, visible]);
  useLayoutEffect(function () {
    var shouldUpdate = true;

    var updatePopover = function updatePopover() {
      if (shouldUpdate && hoveredElementRef && tooltipRef.current) {
        var childRect = hoveredElementRef.getBoundingClientRect();
        var popoverRect = tooltipRef.current.getBoundingClientRect();
        var newCoordinates = getNextTooltipCoordinates(position);

        if (childRect != null && popoverRect != null && (!matchesPosition(popoverRect, newCoordinates) || newCoordinates.pointerX !== coordinates.pointerX || newCoordinates.pointerY !== coordinates.pointerY)) {
          setCoordinates(newCoordinates);
        }

        if (shouldUpdate) {
          window.requestAnimationFrame(updatePopover);
        }
      }
    };

    updatePopover();
    return function () {
      shouldUpdate = false;
    };
  }, [tooltipRef, coordinates.left, coordinates.top, coordinates.pointerX, coordinates.pointerY]);
  var cx = classname('ck-adaptive-tooltip', "ck-adaptive-tooltip--color-" + color, "ck-adaptive-tooltip--size-" + size, {
    'ck-adaptive-tooltip--inverted': isInverted,
    'ck-adaptive-tooltip--compact': isCompact
  });
  var element = /*#__PURE__*/React.createElement("div", {
    className: cx,
    ref: tooltipRef,
    style: {
      left: coordinates.left + "px",
      top: coordinates.top + "px"
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "ck-adaptive-tooltip__content"
  }, children), /*#__PURE__*/React.createElement("div", {
    className: "ck-adaptive-tooltip__pointer",
    style: {
      left: coordinates.pointerX + "px",
      top: coordinates.pointerY + "px",
      rotate: POINTER_ROTATION[coordinates.position] + "deg"
    }
  }));
  return /*#__PURE__*/createPortal(element, document.body);
};

AdaptiveTooltipContent.propTypes = {
  visible: PropTypes.bool.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  isInverted: PropTypes.bool.isRequired,
  size: PropTypes.oneOf([sizes.X3, sizes.X4, sizes.X5, sizes.X6, sizes.X7, sizes.X8]).isRequired,
  color: PropTypes.oneOf([colors.BASE, colors.ERROR]).isRequired,
  isCompact: PropTypes.bool.isRequired,
  floatingPointer: PropTypes.bool.isRequired,
  position: PropTypes.oneOf(positions.ALL).isRequired,
  boundaryElement: PropTypes.oneOfType([PropTypes.node, PropTypes.element, PropTypes.object]).isRequired,
  targetOffset: PropTypes.number.isRequired,
  hoveredElementRef: PropTypes.oneOfType([function () {
    return null;
  }, PropTypes.node]).isRequired
};
export default AdaptiveTooltipContent;
//# sourceMappingURL=adaptive-tooltip-content.js.map